/**
 * Precedent: APR's Scoring Details modal — `apps/portal/web/src/pages/apr/components/
 * ScoringDetailsModal.tsx` (+ `ScoringDetailsModal.css`). Cloned, not imported (D-003).
 *
 * UI_CONVENTIONS §8.5 names it as the precedent for *"the calculation breakdown visible for
 * every score"*, and the shape transfers whole: a modal with a title and the record it is about,
 * a summary strip of tiles, then one sectioned card per factor with its per-term table collapsed
 * underneath — `AnalysisRepository.tsx:876`'s ordering rule, *"the panel leads with the answer,
 * not the arithmetic."* A modal rather than a page because that is what the precedent is, and
 * because every list this opens from is a list of scores: the breakdown explains the row you are
 * looking at, and closing it must put you back on that row.
 *
 * ── WHAT THIS SCREEN IS FOR ────────────────────────────────────────────────────────────
 *
 * §5 Phase 3's gate is *"calculation breakdown visible for every score"*, and §7b.10 says why it
 * is a server surface rather than a screen decoration: *"a breakdown rebuilt in the browser is a
 * second implementation of §6 — and one that can agree with the total while disagreeing about
 * why."* So **every number, label, formula and caveat below is rendered as the server sent it**,
 * and the screen's job is to make the figure ARGUABLE: each term says where its value came from
 * — entered, overridden, not applicable, or never recorded — and carries a control that opens the
 * cell that produced it. Somebody who disagrees with a score can see which cell to go and fix.
 *
 * ── THE ONE PLACE THIS DIVIDES, AND WHY IT IS A UNIT CONVERSION AND NOT A SCORE ────────
 *
 * The two factors carry their per-term contributions in different units, because their inputs
 * are in different units, and the server's own files are explicit about both:
 *
 *   · OVERLAP — `contributionTenThousandths = weightTenths × overlapTenths`, which IS
 *     ten-thousandths already: 150 × 900 = 135000 = 13.50%. `overlap.ts` under `'zero'` sets
 *     `pctTenThousandths = numerator` with no division at all, so the terms sum to the total
 *     exactly and `formatPct` renders each one directly.
 *   · COMPLEXITY — `contributionTenThousandths = weightTenths × capabilityComplexityTenThousandths`
 *     (`score.ts:673`), which carries the weight's own unit as well: 150 × 200000 = 30,000,000.
 *     The total is `numerator / scoredWeightTenths` (`score.ts:688-689`), so a term's share of
 *     that total is `contribution / scoredWeightTenths` — removing the weight unit with the same
 *     denominator the server used, and nothing else. That is the whole of `shareOfTotal` below.
 *
 * It is a division by a number the payload supplies, in the unit system `units.ts` defines, using
 * the package's own `roundHalfUp`. It is not a second implementation of §6.3: the complexity
 * index, each band, each capability's complexity and the total all arrive computed, and this
 * changes none of them. It is here because a column reading `30000000` explains nothing, and the
 * screen exists to explain.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPct, roundHalfUp } from '@platform/erp-scoring';
import { erpApi } from '../api';
import { CHIP_INFO, CHIP_NEUTRAL, CHIP_WARNING, type ChipPair } from '../chips';
import { QUADRANT_CHIP } from '../quadrantChips';
import { tenthsToText } from '../weights';
import {
  type ComplexityTerm,
  type OverlapTerm,
  type ScoringContext,
  type TermSource,
  type UnitScoreDetail,
} from '../types';
import '../chrome/panelWidths.css';
import './CalculationBreakdown.css';

export interface CalculationBreakdownProps {
  initiativeId: string;
  assessmentUnitId: string;
  onClose: () => void;
}

/**
 * Where a value came from, as a chip — **and the normal case wears no colour**.
 *
 * §1.9.3.5, "say it once": if every row said "entered" in a coloured chip, the three rows that
 * are NOT entered would stop standing out, which is the only thing this column is for. The three
 * pairs are §1.9.2's measured ones through `../chips.ts`.
 */
const SOURCE_CHIP: Record<TermSource, { label: string; pair: ChipPair | null; title: string }> = {
  entered: { label: 'entered', pair: null, title: 'Recorded in the grid' },
  override: {
    label: 'overridden',
    pair: CHIP_INFO,
    title: 'Replaced on purpose — the reason is stored with it, and the entered value is kept',
  },
  not_applicable: {
    label: 'not applicable',
    pair: CHIP_NEUTRAL,
    title: 'Stated as not applying to this unit, and scored by the N/A rule',
  },
  missing: {
    label: 'never recorded',
    pair: CHIP_WARNING,
    title: 'No cell at all — scored as 0% in the lowest complexity band, and counted',
  },
};

function SourceChip({ source }: { source: TermSource }) {
  const spec = SOURCE_CHIP[source];
  if (spec.pair === null) {
    return (
      <span className="erp-cb-plain" title={spec.title}>
        {spec.label}
      </span>
    );
  }
  return (
    <span
      className="erp-cb-chip"
      style={{ background: spec.pair.bg, color: spec.pair.fg }}
      title={spec.title}
    >
      {spec.label}
    </span>
  );
}

/** See this file's header: the complexity term's share of its own total, in ten-thousandths. */
const shareOfTotal = (contributionTenThousandths: number, scoredWeightTenths: number): string =>
  scoredWeightTenths === 0
    ? '—'
    : formatPct(roundHalfUp(contributionTenThousandths, scoredWeightTenths));

/** A band, named by the boundaries the server returned — `1`, `2`, `3`, `4-5`, `6+`. */
function bandLabel(index: number, boundaries: readonly number[], floor: number): string {
  /* `floor` is where band 1 starts: 1 for systems, 0 for interfaces (D-022). Starting both at
     1 labelled interfaces band 1 "1-5" when a capability with no interfaces is in it too. */
  const from = index === 0 ? floor : (boundaries[index - 1] ?? 0) + 1;
  const upper = boundaries[index];
  if (upper === undefined) return `${from}+`;
  return from === upper ? `${upper}` : `${from}-${upper}`;
}

export function CalculationBreakdown({
  initiativeId,
  assessmentUnitId,
  onClose,
}: CalculationBreakdownProps) {
  const navigate = useNavigate();
  const [score, setScore] = useState<UnitScoreDetail | null>(null);
  const [scoring, setScoring] = useState<ScoringContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* Collapsed by default — the panel leads with the answer, not the arithmetic. */
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (key: string) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await erpApi.scores.forUnit(initiativeId, assessmentUnitId);
      setScore(payload.score);
      setScoring(payload.scoring);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load this score');
    } finally {
      setLoading(false);
    }
  }, [initiativeId, assessmentUnitId]);

  useEffect(() => {
    void load();
  }, [load]);

  /* Escape closes it, as the help drawer does (`chrome/HelpPanel.tsx`). */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  /**
   * THE CONTROL THAT MAKES THE NUMBER ARGUABLE: open the grid at the cell behind this term.
   *
   * The grid is scoped to one functional area and filtered by business unit, and both are on the
   * score — so the link lands on exactly the row and column the term came from, rather than on a
   * screen the reader then has to navigate.
   */
  const openCell = () => {
    if (score === null) return;
    const params = new URLSearchParams({
      initiative: initiativeId,
      area: score.functionalAreaCapabilityId,
      bu: score.businessUnitId,
    });
    navigate(`/erp_consolidation/assessment-grid?${params.toString()}`);
    onClose();
  };

  const breakdown = score?.breakdown ?? null;

  return (
    /* A right-side panel since D-017 (`CalculationBreakdown.css` carries the geometry and its
       citations). Click-to-close went with the backdrop: the sheet is transparent and passes its
       clicks through to the table behind, so there is nothing to click away — and there should not
       be, because a stray click used to shut this panel. The ✕ in the header and Close in the
       footer are the way out, and both were already here. */
    <div className="erp-cb-overlay">
      <div className="erp-cb-modal">
        <div className="erp-cb-head">
          <div className="erp-cb-head-row">
            <div>
              <h2 className="erp-cb-title">How this score was calculated</h2>
              <div className="erp-cb-sub">
                <span className="erp-cb-unit">
                  {score === null
                    ? '…'
                    : `${score.businessUnitName ?? score.businessUnitId} × ${
                        score.application_name ?? score.applicationId
                      }`}
                </span>
                {score?.application_id_code && (
                  <span className="erp-cb-code">{score.application_id_code}</span>
                )}
                {score?.functionalAreaName && (
                  <span className="erp-cb-code">{score.functionalAreaName}</span>
                )}
              </div>
            </div>
            <button type="button" className="erp-cb-x" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
        </div>

        <div className="erp-cb-body">
          {loading ? (
            <div className="erp-cb-state">
              <h3>Loading the breakdown…</h3>
              <p>Every term is computed by the server, so nothing is estimated here.</p>
            </div>
          ) : error !== null ? (
            <div className="erp-cb-state">
              <h3>⚠️ {error}</h3>
              <button type="button" className="erp-cb-retry" onClick={() => void load()}>
                Retry
              </button>
            </div>
          ) : score === null || breakdown === null ? (
            <div className="erp-cb-state">
              <h3>No score for this unit</h3>
              <p>Nothing has been assessed for it yet.</p>
            </div>
          ) : (
            <div className="erp-cb-stack">
              {/* ── The answer, before any of the arithmetic ──────────────────────── */}
              <div className="erp-cb-section">
                <div className="erp-cb-section-head">
                  <h3>The result</h3>
                  <span className="erp-cb-section-count">
                    rank {score.priorityRank} of {scoring?.unitsInInitiative ?? '—'}
                  </span>
                </div>
                <div className="erp-cb-section-body">
                  <div className="erp-cb-tiles">
                    <div className="erp-cb-tile">
                      <div className="erp-cb-tile-value">{score.overlapDisplay}</div>
                      <div className="erp-cb-tile-label">Weighted overlap</div>
                    </div>
                    <div className="erp-cb-tile">
                      <div className="erp-cb-tile-value">{score.complexityDisplay}</div>
                      <div className="erp-cb-tile-label">
                        Complexity · {breakdown.complexity.calculatedLabel}
                      </div>
                    </div>
                    <div className="erp-cb-tile">
                      <div className="erp-cb-tile-value">{score.importanceDisplay}</div>
                      <div className="erp-cb-tile-label">Importance</div>
                    </div>
                    <div className="erp-cb-tile">
                      <div
                        className="erp-cb-tile-chip"
                        style={{
                          background: QUADRANT_CHIP[score.quadrant].pair.bg,
                          color: QUADRANT_CHIP[score.quadrant].pair.fg,
                        }}
                        title={QUADRANT_CHIP[score.quadrant].meaning}
                      >
                        {score.quadrant}
                      </div>
                      <div className="erp-cb-tile-label">Quadrant</div>
                    </div>
                    <div className="erp-cb-tile">
                      <div className="erp-cb-tile-value">{score.priorityRank}</div>
                      <div className="erp-cb-tile-label">Priority rank</div>
                    </div>
                  </div>

                  {/*
                    COVERAGE AND COMPLETENESS — the two sentences that say how much of the model
                    the figure above actually rests on. §6.2's coverage is the server's own
                    wording ("scored on 80% of the model"), produced by `formatCoverage` rather
                    than composed here, *"because two renderers eventually say two things."*
                  */}
                  {breakdown.overlap.coverageTenths < 1000 && (
                    <>
                      <div className="erp-cb-bar" style={{ marginTop: 16 }}>
                        <div className="erp-cb-bar-track">
                          <div
                            className="erp-cb-bar-fill"
                            style={{ width: `${breakdown.overlap.coverageTenths / 10}%` }}
                          />
                        </div>
                        <span className="erp-cb-bar-pct">{breakdown.overlap.coverageLabel}</span>
                      </div>
                      <p className="erp-cb-note">
                        Capabilities marked not applicable are excluded and the remaining weights
                        scaled back to 100% for this unit, so the figure above is a score over the
                        part of the model that applies.
                      </p>
                    </>
                  )}

                  <div className="erp-cb-bar" style={{ marginTop: 16 }}>
                    <div className="erp-cb-bar-track">
                      <div
                        className="erp-cb-bar-fill"
                        style={{ width: `${breakdown.completeness.completeTenths / 10}%` }}
                      />
                    </div>
                    <span
                      className={`erp-cb-bar-pct${breakdown.completeness.complete ? ' is-full' : ''}`}
                    >
                      {tenthsToText(breakdown.completeness.completeTenths)}% recorded
                    </span>
                  </div>
                  <p className="erp-cb-note">
                    {breakdown.completeness.cellsPresent} of {breakdown.completeness.capabilities}{' '}
                    capabilities have a cell · {breakdown.completeness.notApplicable} not applicable
                    · {breakdown.completeness.missing} never recorded.
                    {breakdown.completeness.missing > 0 &&
                      ' A capability with no cell is scored as 0% overlap in the lowest complexity band and still counted, so this score is a floor rather than an estimate.'}
                  </p>
                </div>
              </div>

              {/* ── Weighted overlap ─────────────────────────────────────────────── */}
              <div className="erp-cb-section">
                <div className="erp-cb-section-head">
                  <h3>Weighted overlap — {breakdown.overlap.totalDisplay}</h3>
                  <span className="erp-cb-section-count">
                    {breakdown.overlap.terms.length} capabilit
                    {breakdown.overlap.terms.length === 1 ? 'y' : 'ies'}
                  </span>
                </div>
                <div className="erp-cb-section-body">
                  <code className="erp-cb-formula">{breakdown.overlap.formula}</code>
                  <p className="erp-cb-prose">{breakdown.overlap.naOptionMeaning}</p>
                  <div className="erp-cb-facts">
                    <div className="erp-cb-fact">
                      <span className="erp-cb-fact-label">Weight scored on</span>
                      <span className="erp-cb-fact-value">
                        {tenthsToText(breakdown.overlap.scoredWeightTenths)}%
                      </span>
                    </div>
                    <div className="erp-cb-fact">
                      <span className="erp-cb-fact-label">Exact, or rounded</span>
                      <span className="erp-cb-fact-value">
                        {breakdown.overlap.exact ? 'exact' : 'rounded half-up'}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="erp-cb-toggle"
                    onClick={() => toggle('overlap')}
                    aria-expanded={open.overlap === true}
                  >
                    {open.overlap === true ? '▲ Hide the terms' : '▼ Show every term'}
                  </button>

                  {open.overlap === true && (
                    <div className="erp-cb-terms-wrap">
                      <table className="erp-cb-terms">
                        <thead>
                          <tr>
                            <th>Capability</th>
                            <th>Weight</th>
                            <th>Overlap</th>
                            <th>From</th>
                            <th>Confidence</th>
                            <th>Contributed</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {breakdown.overlap.terms.map((term: OverlapTerm) => (
                            <tr
                              key={term.capabilityId}
                              className={term.excluded ? 'is-excluded' : undefined}
                            >
                              <td>
                                {term.capabilityCode && (
                                  <span className="erp-cb-cap-code">{term.capabilityCode}</span>
                                )}
                                <span className="erp-cb-cap-name">{term.capabilityName}</span>
                              </td>
                              <td
                                title={`${tenthsToText(term.modelEffectiveWeightTenths)}% of the whole model`}
                              >
                                {tenthsToText(term.weightTenths)}%
                              </td>
                              <td
                                title={
                                  term.overlapOverridePctTenths === null
                                    ? undefined
                                    : `Entered ${
                                        term.overlapPctTenths === null
                                          ? 'nothing'
                                          : `${tenthsToText(term.overlapPctTenths)}%`
                                      }, overridden to ${tenthsToText(term.overlapOverridePctTenths)}%`
                                }
                              >
                                {tenthsToText(term.effectiveOverlapPctTenths)}%
                              </td>
                              <td>
                                <SourceChip source={term.source} />
                              </td>
                              <td>
                                {term.confidencePctTenths === null
                                  ? '—'
                                  : `${tenthsToText(term.confidencePctTenths)}%`}
                              </td>
                              <td
                                title={
                                  term.excluded
                                    ? 'Excluded from the sum and its weight renormalized away'
                                    : `${tenthsToText(term.weightTenths)}% × ${tenthsToText(term.effectiveOverlapPctTenths)}%`
                                }
                              >
                                {term.excluded
                                  ? 'excluded'
                                  : formatPct(term.contributionTenThousandths)}
                              </td>
                              <td>
                                <button type="button" className="erp-cb-link" onClick={openCell}>
                                  Open the cell
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td>Weighted overlap</td>
                            <td>{tenthsToText(breakdown.overlap.scoredWeightTenths)}%</td>
                            <td colSpan={3} />
                            <td>{breakdown.overlap.totalDisplay}</td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                      <p className="erp-cb-note">
                        Numerator {breakdown.overlap.numerator} over denominator{' '}
                        {breakdown.overlap.denominator}, in the integer units the whole module
                        computes in — which is why the total is exact rather than a float that
                        nearly agrees.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Complexity ───────────────────────────────────────────────────── */}
              <div className="erp-cb-section">
                <div className="erp-cb-section-head">
                  <h3>
                    Complexity — {breakdown.complexity.totalDisplay} ·{' '}
                    {breakdown.complexity.calculatedLabel}
                  </h3>
                  <span className="erp-cb-section-count">
                    {breakdown.complexity.terms.length} capabilit
                    {breakdown.complexity.terms.length === 1 ? 'y' : 'ies'}
                  </span>
                </div>
                <div className="erp-cb-section-body">
                  <code className="erp-cb-formula">{breakdown.complexity.formula}</code>
                  <code className="erp-cb-formula">{breakdown.complexity.capabilityFormula}</code>
                  <div className="erp-cb-facts">
                    <div className="erp-cb-fact">
                      <span className="erp-cb-fact-label">Systems factor</span>
                      <span className="erp-cb-fact-value">
                        {tenthsToText(breakdown.complexity.systemsFactorWeightTenths)}%
                      </span>
                    </div>
                    <div className="erp-cb-fact">
                      <span className="erp-cb-fact-label">Interfaces factor</span>
                      <span className="erp-cb-fact-value">
                        {tenthsToText(breakdown.complexity.interfacesFactorWeightTenths)}%
                      </span>
                    </div>
                    <div className="erp-cb-fact">
                      <span className="erp-cb-fact-label">Systems bands</span>
                      <span className="erp-cb-fact-value">
                        {breakdown.complexity.systemsBandValuesTenths
                          .map(
                            (value, index) =>
                              `${bandLabel(index, breakdown.complexity.systemsBandBoundaries, 1)} → ${tenthsToText(value)}%`,
                          )
                          .join(' · ')}
                      </span>
                    </div>
                    <div className="erp-cb-fact">
                      <span className="erp-cb-fact-label">Interface bands</span>
                      <span className="erp-cb-fact-value">
                        {breakdown.complexity.interfacesBandValuesTenths
                          .map(
                            (value, index) =>
                              `${bandLabel(index, breakdown.complexity.interfacesBandBoundaries, 0)} → ${tenthsToText(value)}%`,
                          )
                          .join(' · ')}
                      </span>
                    </div>
                  </div>
                  <p className="erp-cb-prose">{breakdown.complexity.naHandling}</p>

                  <button
                    type="button"
                    className="erp-cb-toggle"
                    onClick={() => toggle('complexity')}
                    aria-expanded={open.complexity === true}
                  >
                    {open.complexity === true ? '▲ Hide the terms' : '▼ Show every term'}
                  </button>

                  {open.complexity === true && (
                    <div className="erp-cb-terms-wrap">
                      <table className="erp-cb-terms">
                        <thead>
                          <tr>
                            <th>Capability</th>
                            <th>Weight</th>
                            <th>Systems</th>
                            <th>Band</th>
                            <th>Interfaces</th>
                            <th>Band</th>
                            <th>Complexity</th>
                            <th>Contributed</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {breakdown.complexity.terms.map((term: ComplexityTerm) => (
                            <tr
                              key={term.capabilityId}
                              className={term.excluded ? 'is-excluded' : undefined}
                            >
                              <td>
                                {term.capabilityCode && (
                                  <span className="erp-cb-cap-code">{term.capabilityCode}</span>
                                )}
                                <span className="erp-cb-cap-name">{term.capabilityName}</span>
                              </td>
                              <td>{tenthsToText(term.weightTenths)}%</td>
                              <td>
                                {term.effectiveSystemsCount}{' '}
                                <SourceChip source={term.systemsSource} />
                              </td>
                              <td>
                                {bandLabel(
                                  term.systemsBandIndex,
                                  breakdown.complexity.systemsBandBoundaries,
                                  1,
                                )}{' '}
                                · {tenthsToText(term.systemsBandTenths)}%
                              </td>
                              <td>
                                {term.effectiveInterfacesCount}{' '}
                                <SourceChip source={term.interfacesSource} />
                              </td>
                              <td>
                                {bandLabel(
                                  term.interfacesBandIndex,
                                  breakdown.complexity.interfacesBandBoundaries,
                                  0,
                                )}{' '}
                                · {tenthsToText(term.interfacesBandTenths)}%
                              </td>
                              <td>{formatPct(term.capabilityComplexityTenThousandths)}</td>
                              <td
                                title={`${tenthsToText(term.weightTenths)}% × ${formatPct(term.capabilityComplexityTenThousandths)}`}
                              >
                                {term.excluded
                                  ? 'excluded'
                                  : shareOfTotal(
                                      term.contributionTenThousandths,
                                      breakdown.complexity.scoredWeightTenths,
                                    )}
                              </td>
                              <td>
                                <button type="button" className="erp-cb-link" onClick={openCell}>
                                  Open the cell
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td>Complexity index</td>
                            <td>{tenthsToText(breakdown.complexity.scoredWeightTenths)}%</td>
                            <td colSpan={5} />
                            <td>{breakdown.complexity.totalDisplay}</td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                      <p className="erp-cb-note">
                        The band boundaries are fixed by the specification and are not configurable;
                        the value each band yields, and the two factor weights, are on the Scoring
                        Definition screen. The label{' '}
                        <strong>{breakdown.complexity.calculatedLabel}</strong> comes from the
                        boundaries{' '}
                        {breakdown.complexity.labelBoundariesTenths
                          .map((tenths) => `${tenthsToText(tenths)}%`)
                          .join(' / ')}
                        .
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Importance ───────────────────────────────────────────────────── */}
              <div className="erp-cb-section">
                <div className="erp-cb-section-head">
                  <h3>Importance — {breakdown.importance.totalDisplay}</h3>
                  <span className="erp-cb-section-count">
                    {breakdown.importance.banding} across {breakdown.importance.unitsInInitiative}{' '}
                    units
                  </span>
                </div>
                <div className="erp-cb-section-body">
                  <code className="erp-cb-formula">{breakdown.importance.formula}</code>
                  <p className="erp-cb-prose">{breakdown.importance.bandingDescription}</p>
                  <div className="erp-cb-facts">
                    <div className="erp-cb-fact">
                      <span className="erp-cb-fact-label">Users</span>
                      <span className="erp-cb-fact-value">
                        {breakdown.importance.usersCount ?? '—'}
                      </span>
                    </div>
                    <div className="erp-cb-fact">
                      <span className="erp-cb-fact-label">Users rank · band</span>
                      <span className="erp-cb-fact-value">
                        {breakdown.importance.usersRank} ·{' '}
                        {tenthsToText(breakdown.importance.usersBandTenths)}%
                      </span>
                    </div>
                    <div className="erp-cb-fact">
                      <span className="erp-cb-fact-label">Users weight</span>
                      <span className="erp-cb-fact-value">
                        {tenthsToText(breakdown.importance.usersWeightTenths)}%
                      </span>
                    </div>
                    <div className="erp-cb-fact">
                      <span className="erp-cb-fact-label">Revenue</span>
                      <span className="erp-cb-fact-value">
                        {breakdown.importance.revenueAmount ?? '—'}
                      </span>
                    </div>
                    <div className="erp-cb-fact">
                      <span className="erp-cb-fact-label">Revenue rank · band</span>
                      <span className="erp-cb-fact-value">
                        {breakdown.importance.revenueRank} ·{' '}
                        {tenthsToText(breakdown.importance.revenueBandTenths)}%
                      </span>
                    </div>
                    <div className="erp-cb-fact">
                      <span className="erp-cb-fact-label">Revenue weight</span>
                      <span className="erp-cb-fact-value">
                        {tenthsToText(breakdown.importance.revenueWeightTenths)}%
                      </span>
                    </div>
                  </div>
                  <p className="erp-cb-note">
                    Both figures are banded <strong>relative to the whole initiative</strong>, which
                    is why neither is stored: a band on a row would go stale the moment any other
                    unit&rsquo;s figures changed.
                  </p>
                  {breakdown.importance.caveat !== null && (
                    <p className="erp-cb-note">{breakdown.importance.caveat}</p>
                  )}
                </div>
              </div>

              {/* ── The quadrant ─────────────────────────────────────────────────── */}
              <div className="erp-cb-section">
                <div className="erp-cb-section-head">
                  <h3>Quadrant — {breakdown.quadrant.quadrant}</h3>
                  <span className="erp-cb-section-count">
                    priority order: {breakdown.quadrant.priorityOrder.join(' › ')}
                  </span>
                </div>
                <div className="erp-cb-section-body">
                  {/* The server's own sentence. §6.5's rule, not a restatement of it. */}
                  <p className="erp-cb-prose">{breakdown.quadrant.rule}</p>
                  <div className="erp-cb-facts">
                    <div className="erp-cb-fact">
                      <span className="erp-cb-fact-label">Overlap vs threshold</span>
                      <span className="erp-cb-fact-value">
                        {formatPct(breakdown.quadrant.overlapTenThousandths)} vs{' '}
                        {formatPct(breakdown.quadrant.overlapThresholdTenThousandths)} ·{' '}
                        {breakdown.quadrant.highOverlap ? 'high' : 'low'}
                      </span>
                    </div>
                    <div className="erp-cb-fact">
                      <span className="erp-cb-fact-label">Complexity vs threshold</span>
                      <span className="erp-cb-fact-value">
                        {formatPct(breakdown.quadrant.complexityTenThousandths)} vs{' '}
                        {formatPct(breakdown.quadrant.complexityThresholdTenThousandths)} ·{' '}
                        {breakdown.quadrant.highComplexity ? 'high' : 'low'}
                      </span>
                    </div>
                  </div>
                  <p className="erp-cb-note">
                    There is no way to move a unit between quadrants directly, and that is
                    deliberate: a quadrant is calculated from the two figures above, and both are
                    overridable <strong>at the cell</strong> with a reason — where the correction is
                    visible beside the figure it changed.
                  </p>
                </div>
              </div>

              {/* ── Where the specification is silent ────────────────────────────── */}
              <div className="erp-cb-section">
                <div className="erp-cb-section-head">
                  <h3>Readings the specification does not settle</h3>
                  <span className="erp-cb-section-count">{breakdown.readings.length}</span>
                </div>
                <div className="erp-cb-section-body">
                  <p className="erp-cb-prose">
                    These are the places the business rules are genuinely silent and a choice had to
                    be made. They are returned with every score rather than buried in a file, so a
                    figure can be argued with on the same screen it is read on.
                  </p>
                  <button
                    type="button"
                    className="erp-cb-toggle"
                    onClick={() => toggle('readings')}
                    aria-expanded={open.readings === true}
                  >
                    {open.readings === true ? '▲ Hide them' : '▼ Show them'}
                  </button>
                  {open.readings === true &&
                    breakdown.readings.map((reading) => (
                      <div className="erp-cb-reading" key={reading.rule}>
                        <div className="erp-cb-reading-rule">{reading.rule}</div>
                        <div className="erp-cb-reading-chosen">{reading.chosen}</div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="erp-cb-foot">
          <div className="erp-cb-foot-info">
            {scoring === null
              ? ''
              : `Capability model v${scoring.capabilityModelVersion ?? '—'} · scoring definition: ${
                  scoring.scoringConfigSource === 'stored' ? 'configured' : 'defaults'
                } · N/A rule: ${scoring.naOption} · thresholds ${tenthsToText(
                  scoring.overlapThresholdPctTenths,
                )}% overlap / ${tenthsToText(scoring.complexityThresholdPctTenths)}% complexity`}
          </div>
          <button type="button" className="erp-cb-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
